import * as Automerge from "@automerge/automerge/slim";
import type { Epic, Category, PlanningCycle, PublishedView, QuickNote, SortDirection, SpaceTemplateBundle, TaskSort, TaskTemplate, Todo } from "../../domain/entities.ts";
import { acknowledge, addOutbox, getEntity, getMeta, listOutbox, putEntity, putTodoChangesAtomically, setMeta, type OutboxChange, type StoredEntity } from "../storage/indexed-db.ts";
import { documentProjection } from "./document-projection.ts";
import { syncBatch } from "./sync-batch.ts";
import { DailyNoteConflict, dailyNoteId, dailyNoteSeed } from "../../features/quick-notes/daily-notes.ts";

// Load the slim build from a real same-origin WASM resource. Besides keeping
// the JavaScript bundle smaller, this gives browsers and DevTools a stable URL.
await Automerge.initializeWasm("/automerge.wasm");

export type SyncState = "local" | "syncing" | "synced" | "attention" | "offline";
let running: Promise<void> | undefined;
let lastSynchronizationError: string | null = null;
export function synchronizationError(): string | null { return lastSynchronizationError; }

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function base64ToBytes(value: string): Uint8Array {
  return Uint8Array.from(atob(value), character => character.charCodeAt(0));
}

export async function changeEpic(id: string, rootId: string, actorId: string, update: Partial<Pick<Epic, "name" | "description" | "color" | "archivedAt">>): Promise<StoredEntity> {
  const current = await getEntity(id);
  if (current && (current.kind !== "epic" || current.rootId !== rootId)) throw new Error("Goal identity cannot change");
  const now = Math.max(Date.now(), (current?.updatedAt || 0) + 1);
  let document = current ? Automerge.load<Epic>(current.snapshot, { actor: actorId }) : Automerge.init<Epic>({ actor: actorId });
  document = Automerge.change(document, draft => {
    if (!current) Object.assign(draft, { id, rootId, name: "", description: "", archivedAt: null, deleted: false, createdAt: now, updatedAt: now });
    Object.assign(draft, update);
    draft.updatedAt = now;
  });
  const change = Automerge.getLastLocalChange(document)!;
  const entity: StoredEntity = { id, rootId, kind: "epic", snapshot: Automerge.save(document), projection: documentProjection(document) as Epic, updatedAt: now };
  await putTodoChangesAtomically([{ entity, outbox: { hash: Automerge.decodeChange(change).hash, entityId: id, rootId, kind: "epic", bytes: bytesToBase64(change), projection: entity.projection, createdAt: now } }]);
  return entity;
}

export async function changeTodo(id: string, rootId: string, actorId: string, mutate: (todo: Todo) => void): Promise<StoredEntity> {
  return (await changeTodoBatch([{ id, rootId, mutate }], actorId))[0];
}

export async function changeTodoBatch(items: Array<{ id: string; rootId: string; mutate(todo: Todo): void }>, actorId: string): Promise<StoredEntity[]> {
  if (items.length > 250 || new Set(items.map(item => item.id)).size !== items.length) throw new Error("Invalid task change batch");
  const batchId = items.length > 1 ? crypto.randomUUID() : undefined;
  const prepared = await Promise.all(items.map(async ({ id, rootId, mutate }, index) => {
    const current = await getEntity(id);
    if (current && (current.kind !== "todo" || current.rootId !== rootId)) throw new Error("Task identity cannot change");
    const previous = current?.projection as Todo | undefined;
    // Resolve before entering the synchronous Automerge callback.
    const parent = previous?.parentTaskId ? (await getEntity(previous.parentTaskId))?.projection as Todo | undefined : undefined;
    const now = Math.max(Date.now() + index, Number((current?.projection as Todo | undefined)?.updatedAt || 0) + 1);
    let document = current ? Automerge.load<Todo>(current.snapshot, { actor: actorId }) : Automerge.init<Todo>({ actor: actorId });
    document = Automerge.change(document, draft => {
      if (!current) Object.assign(draft, emptyTodo(id, rootId));
      mutate(draft as Todo);
      if (previous?.parentTaskId && !draft.parentTaskId && parent && parent.rootId === rootId) draft.epicId = parent.epicId || null;
      draft.updatedAt = now;
    });
    const change = Automerge.getLastLocalChange(document);
    if (!change) throw new Error("No local change was produced");
    const projection = documentProjection(document) as Todo;
    const entity: StoredEntity = { id, rootId, kind: "todo", snapshot: Automerge.save(document), projection, updatedAt: now };
    const outbox: OutboxChange = { hash: Automerge.decodeChange(change).hash, entityId: id, rootId, kind: "todo", bytes: bytesToBase64(change), projection, createdAt: now, batchId };
    return { entity, outbox };
  }));
  const queuedAt = Date.now();
  prepared.forEach((item, index) => { item.outbox.createdAt = queuedAt + index; });
  await putTodoChangesAtomically(prepared);
  return prepared.map(item => item.entity);
}

export async function changeQuickNote(id: string, rootId: string, actorId: string, update: Partial<Pick<QuickNote, "body" | "pinned" | "archivedAt" | "convertedTaskId" | "linkedTaskIds" | "deleted" | "dailyDate">> & { baseBody?: string }): Promise<StoredEntity> {
  const current = await getEntity(id);
  if (current && current.kind !== "quick_note") throw new Error("Quick note not found");
  if (current && (current.rootId !== rootId || (update.dailyDate !== undefined && (current.projection as QuickNote).dailyDate !== update.dailyDate))) throw new Error("Daily note identity cannot change");
  if (update.dailyDate && id !== await dailyNoteId(rootId, update.dailyDate)) throw new Error("Invalid daily note identity");
  if (current && !update.dailyDate && update.baseBody !== undefined) {
    const note = current.projection as QuickNote;
    if (note.body !== update.baseBody && note.body !== update.body) throw new Error("The saved note changed on another device. Open it from Quick Notes instead of overwriting or undoing this capture.");
  }
  if (current && update.dailyDate && update.baseBody !== undefined) {
    const note = current.projection as QuickNote;
    if ((!note.deleted && note.body !== update.baseBody && note.body !== update.body) || (note.deleted && !!update.baseBody)) throw new DailyNoteConflict();
  }
  const seed = !current && update.dailyDate ? dailyNoteSeed(Automerge, id, rootId, update.dailyDate) : null;
  const pending: Array<{ entity: StoredEntity; outbox: OutboxChange }> = [];
  if (seed) {
    const change = Automerge.getLastLocalChange(seed)!;
    const projection = documentProjection(seed) as QuickNote;
    pending.push({ entity: { id, rootId, kind: "quick_note", snapshot: Automerge.save(seed), projection, updatedAt: 0 }, outbox: { hash: Automerge.decodeChange(change).hash, entityId: id, rootId, kind: "quick_note", bytes: bytesToBase64(change), projection, createdAt: 0 } });
  }
  let document = current
    ? Automerge.load<QuickNote>(current.snapshot, { actor: actorId })
    : seed ? Automerge.clone(seed, { actor: actorId }) : Automerge.init<QuickNote>({ actor: actorId });
  const now = Math.max(Date.now(), (current?.updatedAt || 0) + 1);
  document = Automerge.change(document, draft => {
    if (!current && !seed) Object.assign(draft, { id, rootId, body: "", pinned: false, archivedAt: null, convertedTaskId: null, linkedTaskIds: {}, deleted: false, createdAt: now, updatedAt: now });
    if (seed) draft.createdAt = now;
    if (update.body !== undefined && update.body !== draft.body) Automerge.updateText(draft, ["body"], update.body.slice(0, 100_000));
    if (update.pinned !== undefined) draft.pinned = update.pinned;
    if (update.archivedAt !== undefined) draft.archivedAt = update.archivedAt;
    if (update.convertedTaskId !== undefined) draft.convertedTaskId = update.convertedTaskId;
    if (update.linkedTaskIds !== undefined) {
      draft.linkedTaskIds ||= {};
      for (const [id, linked] of Object.entries(update.linkedTaskIds)) draft.linkedTaskIds[id] = linked;
    }
    if (update.deleted !== undefined) draft.deleted = update.deleted;
    draft.updatedAt = now;
  });
  const change = Automerge.getLastLocalChange(document);
  if (!change) throw new Error("No local change was produced");
  const projection = documentProjection(document) as QuickNote;
  const entity: StoredEntity = { id, rootId, kind: "quick_note", snapshot: Automerge.save(document), projection, updatedAt: now };
  pending.push({ entity, outbox: { hash: Automerge.decodeChange(change).hash, entityId: id, rootId, kind: "quick_note", bytes: bytesToBase64(change), projection, createdAt: now } });
  await putTodoChangesAtomically(pending);
  return entity;
}

export async function createCategory(id: string, rootId: string, actorId: string, name: string, viewType: "list" | "board" | "timeline" | "table" = "list", boardColumns: string[] = [], sortBy: TaskSort = "manual", sortDirection: SortDirection = "asc", pageSize = 0, showCompleted = false, defaults: Partial<Category> = {}): Promise<StoredEntity> {
  let document = Automerge.init<Category>({ actor: actorId });
  const now = Date.now();
  document = Automerge.change(document, draft => Object.assign(draft, { id, rootId, name, referenceNote: "", viewType, boardColumns, boardWipLimits: {}, boardColumnSettings: {}, boardCardFields: ["due", "priority", "comments"], boardAutomation: { completedColumn: null, overdueUrgent: false, completeWithSubtasks: false }, approvalRule: null, sortBy, sortDirection, pageSize, showCompleted, defaultPriority: "none", defaultTags: [], defaultAssigneeIds: [], defaultTaskType: "text", defaultTemplateId: null, defaultDueOffsetDays: null, archivedAt: null, ...defaults, deleted: false, createdAt: now, updatedAt: now }));
  const change = Automerge.getLastLocalChange(document)!;
  const projection = documentProjection(document) as Category;
  const entity: StoredEntity = { id, rootId, kind: "category", snapshot: Automerge.save(document), projection, updatedAt: now };
  await putEntity(entity);
  await addOutbox({ hash: Automerge.decodeChange(change).hash, entityId: id, rootId, kind: "category", bytes: bytesToBase64(change), projection, createdAt: now });
  return entity;
}

export async function changeCategory(id: string, rootId: string, actorId: string, mutate: (category: Category) => void): Promise<StoredEntity> {
  const current = await getEntity(id);
  if (!current || current.kind !== "category") throw new Error("Collection not found");
  let document = Automerge.load<Category>(current.snapshot, { actor: actorId });
  document = Automerge.change(document, draft => { mutate(draft as Category); draft.updatedAt = Date.now(); });
  const change = Automerge.getLastLocalChange(document);
  if (!change) throw new Error("No local change was produced");
  const projection = documentProjection(document) as Category;
  const now = Date.now();
  const entity: StoredEntity = { id, rootId, kind: "category", snapshot: Automerge.save(document), projection, updatedAt: now };
  await putEntity(entity);
  await addOutbox({ hash: Automerge.decodeChange(change).hash, entityId: id, rootId, kind: "category", bytes: bytesToBase64(change), projection, createdAt: now });
  return entity;
}

export async function createTemplate(id: string, rootId: string, actorId: string, template: Omit<TaskTemplate, "id" | "rootId" | "createdAt" | "updatedAt">): Promise<StoredEntity> {
  let document = Automerge.init<TaskTemplate>({ actor: actorId });
  const now = Date.now();
  document = Automerge.change(document, draft => Object.assign(draft, { ...template, id, rootId, createdAt: now, updatedAt: now }));
  const change = Automerge.getLastLocalChange(document)!;
  const projection = documentProjection(document) as TaskTemplate;
  const entity: StoredEntity = { id, rootId, kind: "template", snapshot: Automerge.save(document), projection, updatedAt: now };
  await putEntity(entity);
  await addOutbox({ hash: Automerge.decodeChange(change).hash, entityId: id, rootId, kind: "template", bytes: bytesToBase64(change), projection, createdAt: now });
  return entity;
}

export async function changeTemplate(id: string, rootId: string, actorId: string, mutate: (template: TaskTemplate) => void): Promise<StoredEntity> {
  const current = await getEntity(id);
  if (!current || current.kind !== "template") throw new Error("Template not found");
  let document = Automerge.load<TaskTemplate>(current.snapshot, { actor: actorId });
  document = Automerge.change(document, draft => { mutate(draft as TaskTemplate); draft.updatedAt = Date.now(); });
  const change = Automerge.getLastLocalChange(document);
  if (!change) throw new Error("No local change was produced");
  const projection = documentProjection(document) as TaskTemplate;
  const now = Date.now();
  const entity: StoredEntity = { id, rootId, kind: "template", snapshot: Automerge.save(document), projection, updatedAt: now };
  await putEntity(entity);
  await addOutbox({ hash: Automerge.decodeChange(change).hash, entityId: id, rootId, kind: "template", bytes: bytesToBase64(change), projection, createdAt: now });
  return entity;
}

export async function createSpaceTemplateBundle(id: string, rootId: string, actorId: string, bundle: Omit<SpaceTemplateBundle, "id" | "rootId" | "createdAt" | "updatedAt">): Promise<StoredEntity> {
  let document = Automerge.init<SpaceTemplateBundle>({ actor: actorId });
  const now = Date.now();
  document = Automerge.change(document, draft => Object.assign(draft, { ...bundle, id, rootId, createdAt: now, updatedAt: now }));
  const change = Automerge.getLastLocalChange(document)!;
  const projection = documentProjection(document) as SpaceTemplateBundle;
  const entity: StoredEntity = { id, rootId, kind: "space_template", snapshot: Automerge.save(document), projection, updatedAt: now };
  await putEntity(entity);
  await addOutbox({ hash: Automerge.decodeChange(change).hash, entityId: id, rootId, kind: "space_template", bytes: bytesToBase64(change), projection, createdAt: now });
  return entity;
}

export async function deleteSpaceTemplateBundle(id: string, rootId: string, actorId: string): Promise<StoredEntity> {
  const current = await getEntity(id);
  if (!current || current.kind !== "space_template") throw new Error("Space template not found");
  let document = Automerge.load<SpaceTemplateBundle>(current.snapshot, { actor: actorId });
  const now = Math.max(Date.now(), current.updatedAt + 1);
  document = Automerge.change(document, draft => { draft.deleted = true; draft.updatedAt = now; });
  const change = Automerge.getLastLocalChange(document)!;
  const projection = documentProjection(document) as SpaceTemplateBundle;
  const entity: StoredEntity = { id, rootId, kind: "space_template", snapshot: Automerge.save(document), projection, updatedAt: now };
  await putEntity(entity);
  await addOutbox({ hash: Automerge.decodeChange(change).hash, entityId: id, rootId, kind: "space_template", bytes: bytesToBase64(change), projection, createdAt: now });
  return entity;
}

export async function createPublishedView(id: string, rootId: string, actorId: string, view: Omit<PublishedView, "id" | "rootId" | "createdAt" | "updatedAt">): Promise<StoredEntity> {
  let document = Automerge.init<PublishedView>({ actor: actorId }); const now = Date.now();
  document = Automerge.change(document, draft => Object.assign(draft, { ...view, id, rootId, createdAt: now, updatedAt: now }));
  const change = Automerge.getLastLocalChange(document)!; const projection = documentProjection(document) as PublishedView;
  const entity: StoredEntity = { id, rootId, kind: "saved_view", snapshot: Automerge.save(document), projection, updatedAt: now };
  await putEntity(entity); await addOutbox({ hash: Automerge.decodeChange(change).hash, entityId: id, rootId, kind: "saved_view", bytes: bytesToBase64(change), projection, createdAt: now }); return entity;
}

export async function changePublishedView(id: string, rootId: string, actorId: string, mutate: (view: PublishedView) => void): Promise<StoredEntity> {
  const current = await getEntity(id); if (!current || current.kind !== "saved_view") throw new Error("Published view not found");
  let document = Automerge.load<PublishedView>(current.snapshot, { actor: actorId }); document = Automerge.change(document, draft => { mutate(draft as PublishedView); draft.updatedAt = Date.now(); });
  const change = Automerge.getLastLocalChange(document); if (!change) throw new Error("No local change was produced"); const projection = documentProjection(document) as PublishedView; const now = Date.now();
  const entity: StoredEntity = { id, rootId, kind: "saved_view", snapshot: Automerge.save(document), projection, updatedAt: now }; await putEntity(entity); await addOutbox({ hash: Automerge.decodeChange(change).hash, entityId: id, rootId, kind: "saved_view", bytes: bytesToBase64(change), projection, createdAt: now }); return entity;
}

export async function createCycle(id: string, rootId: string, actorId: string, cycle: Omit<PlanningCycle, "id" | "rootId" | "createdAt" | "updatedAt">): Promise<StoredEntity> {
  let document = Automerge.init<PlanningCycle>({ actor: actorId });
  const now = Date.now();
  document = Automerge.change(document, draft => Object.assign(draft, { ...cycle, id, rootId, createdAt: now, updatedAt: now }));
  const change = Automerge.getLastLocalChange(document)!;
  const projection = documentProjection(document) as PlanningCycle;
  const entity: StoredEntity = { id, rootId, kind: "cycle", snapshot: Automerge.save(document), projection, updatedAt: now };
  await putEntity(entity);
  await addOutbox({ hash: Automerge.decodeChange(change).hash, entityId: id, rootId, kind: "cycle", bytes: bytesToBase64(change), projection, createdAt: now });
  return entity;
}

export async function changeCycle(id: string, rootId: string, actorId: string, mutate: (cycle: PlanningCycle) => void): Promise<StoredEntity> {
  const current = await getEntity(id);
  if (!current || current.kind !== "cycle") throw new Error("Cycle not found");
  let document = Automerge.load<PlanningCycle>(current.snapshot, { actor: actorId });
  document = Automerge.change(document, draft => { mutate(draft as PlanningCycle); draft.updatedAt = Date.now(); });
  const change = Automerge.getLastLocalChange(document);
  if (!change) throw new Error("No local change was produced");
  const projection = documentProjection(document) as PlanningCycle;
  const now = Date.now();
  const entity: StoredEntity = { id, rootId, kind: "cycle", snapshot: Automerge.save(document), projection, updatedAt: now };
  await putEntity(entity);
  await addOutbox({ hash: Automerge.decodeChange(change).hash, entityId: id, rootId, kind: "cycle", bytes: bytesToBase64(change), projection, createdAt: now });
  return entity;
}

export function conflictingFields(entity: StoredEntity, actorId: string): string[] {
  if (entity.kind !== "todo") return [];
  const document = Automerge.load<Todo>(entity.snapshot, { actor: actorId });
  return ["title", "description", "type", "subtasks", "position", "pinned", "completed", "completedAt", "priority", "dueDate", "startDate", "startAt", "endAt", "estimatedMinutes", "reminderAt", "reminders", "recurrence", "recurrenceSeriesId", "recurrenceOccurrence", "recurrenceAnchorDate", "comments", "categoryId", "assignedTo", "tags", "tagOrder", "dependencyIds", "relationships", "epicId", "cycleId", "archivedAt", "deleted"].filter(field => {
    const values = Automerge.getConflicts(document, field as keyof Todo);
    return values !== undefined && Object.keys(values).length > 1;
  });
}

export interface FieldConflict { field: keyof Todo; values: unknown[] }

export function conflictValues(entity: StoredEntity, actorId: string): FieldConflict[] {
  if (entity.kind !== "todo") return [];
  const document = Automerge.load<Todo>(entity.snapshot, { actor: actorId });
  return conflictingFields(entity, actorId).map(field => {
    const values = Automerge.getConflicts(document, field as keyof Todo);
    return { field: field as keyof Todo, values: Object.values(values || {}).map(value => documentProjection(value)) };
  });
}

export async function synchronize(actorId: string, update: (state: SyncState) => void): Promise<void> {
  if (running) return running;
  running = syncLoop(actorId, update).finally(() => { running = undefined; });
  return running;
}

async function syncLoop(actorId: string, update: (state: SyncState) => void): Promise<void> {
  if (!navigator.onLine) { update("offline"); return; }
  lastSynchronizationError = null;
  update("syncing");
  try {
    let hasMore = true;
    while (hasMore) {
      const cursor = await getMeta("cursor", 0);
      const changes = syncBatch(await listOutbox());
      const response = await fetch("/api/v1/sync", {
        method: "POST", credentials: "include", headers: { "content-type": "application/json" },
        body: JSON.stringify({ cursor, actor_id: actorId, changes: changes.map(change => ({
          hash: change.hash, entity_id: change.entityId, root_id: change.rootId, kind: change.kind, bytes: change.bytes, projection: change.projection
        })) })
      });
      if (response.status === 401) { update("attention"); return; }
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(typeof body?.error === "string" ? body.error : `Synchronization request failed (HTTP ${response.status})`);
      }
      const body = await response.json();
      await acknowledge(body.acknowledged);
      for (const snapshot of body.snapshots || []) await applyRemoteSnapshot(snapshot, actorId, body.cursor);
      for (const remote of body.changes) await applyRemote(remote, actorId);
      await setMeta("cursor", body.cursor);
      hasMore = body.has_more || (await listOutbox()).length > 0;
    }
    update("synced");
  } catch (error) {
    lastSynchronizationError = error instanceof Error ? error.message : "Synchronization could not finish.";
    console.error(error);
    update(!navigator.onLine || error instanceof TypeError ? "offline" : "attention");
  }
}

async function applyRemoteSnapshot(remote: any, actorId: string, cursor: number): Promise<void> {
  const snapshot = base64ToBytes(remote.snapshot);
  Automerge.load<any>(snapshot, { actor: actorId });
  await putEntity({ id: remote.entity_id, rootId: remote.root_id, kind: remote.kind, snapshot, projection: remote.projection, updatedAt: cursor || Date.now() });
}

async function applyRemote(remote: any, actorId: string): Promise<void> {
  const current = await getEntity(remote.entity_id);
  let document = current
    ? Automerge.load<any>(current.snapshot, { actor: actorId })
    : Automerge.init<any>({ actor: actorId });
  try {
    [document] = Automerge.applyChanges(document, [base64ToBytes(remote.bytes)]);
  } catch (error) {
    // An echoed local change may already be present in the saved document.
    if (!current) throw error;
  }
  const projection = documentProjection(document) as Todo | Category | TaskTemplate | PlanningCycle | QuickNote | PublishedView;
  await putEntity({ id: remote.entity_id, rootId: remote.root_id, kind: remote.kind, snapshot: Automerge.save(document), projection, updatedAt: Date.now() });
}

function emptyTodo(id: string, rootId: string): Todo {
  const now = Date.now();
  return { id, rootId, title: "", description: "", type: "text", subtasks: [], position: now, pinned: false, completed: false, completedAt: null, priority: "none", dueDate: null, startDate: null, startAt: null, endAt: null, estimatedMinutes: null, reminderAt: null, reminders: [], recurrence: null, recurrenceSeriesId: null, recurrenceOccurrence: 0, recurrenceAnchorDate: null, comments: [], categoryId: null, assignedTo: [], tags: {}, tagOrder: [], dependencyIds: [], relationships: [], cycleId: null, externalCalendars: [], archivedAt: null, deleted: false, createdAt: now, updatedAt: now };
}
